import { useQuery } from '@apollo/client'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  type RouteObject,
  RouterProvider,
  RoutesProvider,
  ScrollRestoration,
} from '@faceup/router'
import { FuAdminRoleType, Institution } from '@faceup/utils'
import { wrapCreateBrowserRouterV7 } from '@sentry/react'
import { graphql } from '../__generated__'
import { AdminLayout } from '../AdminLayout/AdminLayout'
import { PageSuspense } from '../Components/PageSuspense'
import { AdminAccessRightsProvider } from '../Contexts/AdminAccessRightsProvider'
import { routes, useRoutes } from '../hooks/useRoutes'
import { RouteNotFound } from '../RouteNotFound'
import { Auth } from '../utils/auth'
import { AiPromptsView } from './AiPromptsView/AiPromptsView'
import { CreateAdminView } from './CreateAdminView'
import { GlobalTablesView } from './GlobalTablesView'
import { HotlinesView } from './HotlinesView'
import { InstitutionDetailView } from './InstitutionDetailView'
import { InstitutionsView } from './InstitutionsView'
import { IntegrationHttpLogsView } from './IntegrationHttpLogsView'
import { LoginRedirect } from './LoginView/LoginRedirect'
import { LoginView } from './LoginView/LoginView'
import { MaterialsView } from './MaterialsView/MaterialsView'
import { PartnerDetailView } from './PartnerDetailView'
import { PartnersView } from './PartnersView'
import { ReportDetailView } from './ReportDetailView'
import { ReportsView } from './ReportsView'
import { SendEmailsView } from './SendEmailsView'
import { VonageHotlinesView } from './VonageHotlinesView'

export const query = {
  AppRouterQuery: graphql(`
    query AppRouterQuery {
      fuAdminViewer {
        id
        role
        ...FuAdminLayout_admin
        ...FuViewerProvider_admin
      }
    }
  `),
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouterV7(createBrowserRouter)

export const AppRouter = () => {
  const { client, data } = useQuery(query.AppRouterQuery, {
    onCompleted: async data => {
      // User has invalid JWT
      const viewer = data?.fuAdminViewer
      if (!viewer?.__typename) {
        logout()
      }
    },
    skip: !Auth.getJwt(),
  })

  const routeLinks = useRoutes()

  const viewer = data?.fuAdminViewer?.__typename === 'FuAdmin' ? data.fuAdminViewer : null
  const isAuthenticated = Boolean(viewer) || Boolean(Auth.getJwt())

  const logout = async () => {
    client.stop()
    await client.clearStore()
    Auth.logout()
    window.location.href = '/'
  }

  const unAuthenticatedRoutes: RouteObject[] = [
    {
      index: true,
      Component: LoginView,
    },
    {
      path: '/create-admin',
      Component: CreateAdminView,
    },
    {
      path: '*',
      Component: LoginRedirect,
    },
  ]

  const role = viewer?.role
  const isPsychologist = role === FuAdminRoleType.Psychologist
  const isManagerOrAdmin = role === FuAdminRoleType.Manager || role === FuAdminRoleType.Admin
  const isAdmin = role === FuAdminRoleType.Admin
  const isIntegrationDeveloper = role === FuAdminRoleType.IntegrationDeveloper
  // ENG-2130: the HTTP logs are visible to the admin and integration developer roles
  const canSeeIntegrationHttpLogs = isAdmin || isIntegrationDeveloper

  const authenticatedRoutes: RouteObject[] = [
    {
      path: routes.home,
      element: (
        <AdminLayout logout={logout} viewer={viewer}>
          <AdminAccessRightsProvider viewer={viewer}>
            <PageSuspense>
              <Outlet />
            </PageSuspense>
          </AdminAccessRightsProvider>
        </AdminLayout>
      ),
      children: [
        {
          path: '/',
          element: isPsychologist ? (
            <Navigate replace to={routes => routes.schools()} />
          ) : isIntegrationDeveloper ? (
            <Navigate replace to={routes => routes.integrationHttpLogs()} />
          ) : (
            <Navigate replace to={routes => routes.companies()} />
          ),
        },
        {
          path: routes.schools,
          element: <InstitutionsView type={Institution.School} />,
        },
        {
          path: routes.companies,
          element: <InstitutionsView type={Institution.Company} />,
        },
        {
          path: routes.institution,
          Component: InstitutionDetailView,
        },
        ...(isManagerOrAdmin
          ? [
              {
                path: routes.partners,
                Component: PartnersView,
              },
              {
                path: routes.partner,
                Component: PartnerDetailView,
              },
              {
                path: routes.materials,
                Component: MaterialsView,
              },
            ]
          : []),
        ...(isPsychologist
          ? [
              {
                path: routes.reports,
                Component: ReportsView,
              },
              {
                path: routes.report,
                Component: ReportDetailView,
              },
            ]
          : []),
        ...(isManagerOrAdmin
          ? [
              {
                path: routes.hotlines,
                Component: HotlinesView,
              },
              {
                path: routes.vonageHotlines,
                Component: VonageHotlinesView,
              },
              {
                path: routes.aiPrompts,
                Component: AiPromptsView,
              },
            ]
          : []),
        ...(isAdmin
          ? [
              {
                path: routes.sendEmails,
                Component: SendEmailsView,
              },
              {
                path: routes.globalTables,
                Component: GlobalTablesView,
              },
            ]
          : []),
        ...(canSeeIntegrationHttpLogs
          ? [
              {
                path: routes.integrationHttpLogs,
                Component: IntegrationHttpLogsView,
              },
            ]
          : []),
        {
          path: '*',
          Component: RouteNotFound,
        },
      ],
    },
  ]

  const router = sentryCreateBrowserRouter([
    {
      path: '/',
      element: (
        <RoutesProvider routes={routeLinks}>
          <ScrollRestoration />
          <Outlet />
        </RoutesProvider>
      ),
      children: isAuthenticated ? authenticatedRoutes : unAuthenticatedRoutes,
    },
  ])

  return <RouterProvider router={router} />
}
